import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { A } from '../lib/theme';

const TYPE_CONFIG = {
    success: { icon: '✅', dot: A.forest,  unreadBg: `${A.forest}10`  },
    warning: { icon: '⚠️',  dot: A.gold,    unreadBg: `${A.gold}12`    },
    error:   { icon: '❌', dot: A.crimson, unreadBg: `${A.crimson}10` },
    info:    { icon: 'ℹ️',  dot: A.gold,    unreadBg: `${A.gold}08`    },
};

const NotificationItem = ({ notification, onRead }) => {
    const { type, title, message, is_read, created_at } = notification;
    const cfg = TYPE_CONFIG[type] || TYPE_CONFIG.info;
    const [expanded, setExpanded] = useState(false);

    // Whether the collapsed view likely clips the content — used to surface a
    // "Read more" affordance so the item reads as clickable/expandable.
    const isClipped = (message || '').length > 70 || (title || '').length > 38;

    const timeStr = (() => {
        try {
            return new Date(created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        } catch {
            return '';
        }
    })();

    const handleClick = (e) => {
        // Defensive: never let the click reach the backdrop/overlay behind the panel.
        e.stopPropagation();
        setExpanded(prev => !prev);
        if (!is_read) onRead(notification.id);
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, height: 0, overflow: 'hidden' }}
            onClick={handleClick}
            style={{
                padding: '11px 16px',
                borderBottom: `1px solid ${A.border}`,
                background: is_read ? 'transparent' : cfg.unreadBg,
                cursor: 'pointer',
                transition: 'background 0.15s',
                position: 'relative',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = A.surface; }}
            onMouseLeave={e => { e.currentTarget.style.background = is_read ? 'transparent' : cfg.unreadBg; }}
        >
            {/* Unread left accent bar */}
            {!is_read && (
                <div style={{
                    position: 'absolute', left: 0, top: 0, bottom: 0,
                    width: 3, background: cfg.dot, borderRadius: '0 2px 2px 0'
                }} />
            )}

            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                {/* Icon */}
                <span style={{ fontSize: 16, flexShrink: 0, marginTop: 1, lineHeight: 1.4 }}>{cfg.icon}</span>

                {/* Content */}
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, marginBottom: 3 }}>
                        <p style={{
                            fontSize: 12, fontWeight: is_read ? 500 : 700,
                            color: is_read ? A.muted : A.dark,
                            margin: 0, lineHeight: 1.3,
                            ...(expanded
                                ? { whiteSpace: 'normal', wordBreak: 'break-word' }
                                : { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }),
                        }}>
                            {title}
                        </p>
                        <span style={{
                            fontSize: 10, color: A.muted, flexShrink: 0,
                            fontFamily: 'monospace', marginTop: 1
                        }}>
                            {timeStr}
                        </span>
                    </div>
                    <p style={{
                        fontSize: 11, color: A.muted, margin: 0,
                        lineHeight: 1.5,
                        ...(expanded
                            ? { whiteSpace: 'pre-wrap', wordBreak: 'break-word' }
                            : { display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }),
                    }}>
                        {message}
                    </p>
                    {(isClipped || expanded) && (
                        <span style={{
                            display: 'inline-block', marginTop: 5,
                            fontSize: 10, fontWeight: 700, letterSpacing: '0.04em',
                            color: A.gold, textTransform: 'uppercase',
                        }}>
                            {expanded ? 'Show less' : 'Read more'}
                        </span>
                    )}
                </div>

                {/* Unread dot */}
                {!is_read && (
                    <div style={{
                        width: 7, height: 7, borderRadius: '50%',
                        background: cfg.dot, flexShrink: 0, marginTop: 5
                    }} />
                )}
            </div>
        </motion.div>
    );
};

export default NotificationItem;
